import TonePill, { TONES, type Tone } from '../billing/TonePill';
import type { HoursSubmissionView } from '../viewModel';
import { PARTIAL_STATUS_PILL } from './partialConfirm';

/**
 * The six persisted hours statuses and the render-only current period, with
 * the prototype's labels and tones (`components/EngagementHub/HoursTab.tsx`):
 * yellow waits on a person, brand is confirmed, red is rejected, muted is
 * withdrawn, purple waits on the expert.
 *
 * The prototype's `auto_approved` and `paid` pills are absent: `auto_approved`
 * was dropped from the domain (`hours.go:111`; TLA INV-H2), so a deemed
 * approval is a `confirmed` pill with a caption, and paid is its own column.
 */
const STATUS_PILL: Record<string, { label: string; tone: Tone }> = {
  submitted: { label: 'Pending approval', tone: TONES.awaiting },
  confirmed: { label: 'Confirmed', tone: TONES.moved },
  escalated: { label: 'Escalated', tone: TONES.awaiting },
  rejected: { label: 'Rejected', tone: TONES.failed },
  withdrawn: { label: 'Withdrawn', tone: TONES.quiet },
  not_yet_submitted: { label: 'Not yet submitted', tone: TONES.working },
  ...PARTIAL_STATUS_PILL,
};

/** What the auto-approve routine writes to `resolution` (`hours_routines.go`). */
const DEEMED_APPROVAL = /^no_response_after_(\d+)d$/;

/**
 * The caption under a deemed approval. The window comes from the machine code,
 * which is never rendered itself; a code of any other shape drops the count
 * rather than guessing it.
 */
function autoApprovalCaption(resolution: string | null): string {
  const days = resolution?.match(DEEMED_APPROVAL)?.[1];
  if (!days) return 'Approved automatically with no review.';
  return `Approved automatically after ${days} ${days === '1' ? 'day' : 'days'} with no review.`;
}

export default function HoursStatusCell({ row }: { row: HoursSubmissionView }): JSX.Element {
  const pill = STATUS_PILL[row.status] ?? { label: row.status, tone: TONES.quiet };
  return (
    <div className="flex flex-col gap-6">
      <TonePill tone={pill.tone}>
        <span className="whitespace-nowrap">{pill.label}</span>
      </TonePill>
      {row.approvedAutomatically ? (
        <span className="max-w-[200px] text-on-surface-muted body-2">
          {autoApprovalCaption(row.resolution)}
        </span>
      ) : null}
    </div>
  );
}
